import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useSnackbar } from "notistack";
import { clientBackendPDF } from "configurations/axios";
import ContableTable from "../components/ContableTable";
import SelectorMesAnio from "../components/SelectorMesAnio";
import usePagosCobrosView from "../hooks/usePagosCobrosView";

const useStyles = makeStyles((theme) => ({
  root: {
    boxSizing: "border-box",
    minHeight: "calc(100vh - 96px)",
    background: "#080f1f",
    padding: theme.spacing(3, 3, 9),
  },
  header: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5),
  },
  title: {
    color: "#f5f7fb",
    fontSize: 19,
    fontWeight: 900,
  },
  /* ── Stat cards ── */
  cards: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    marginBottom: theme.spacing(3),
  },
  card: {
    background: "#111827",
    border: "1px solid #1d3454",
    borderRadius: 10,
    padding: theme.spacing(2, 2.5),
  },
  cardLabel: {
    color: "#53647e",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 1.2,
    marginBottom: theme.spacing(0.8),
    textTransform: "uppercase",
  },
  cardValue: {
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1,
  },
  cardValueSuccess: { color: "#02dfb1" },
  cardValueDanger:  { color: "#ff405d" },
  cardValueWarning: { color: "#ffb229" },
  cardValueInfo:    { color: "#19b8f2" },
  cardSub: {
    color: "#53647e",
    fontSize: 11,
    marginTop: theme.spacing(0.5),
  },
  /* ── Table helpers ── */
  number: { color: "#bcd8f8", fontWeight: 900 },
  emisor:  { color: "#acd0f6", fontSize: 13 },
  badge: {
    background: "#1a2e4a",
    borderRadius: 12,
    color: "#8bbcff",
    display: "inline-block",
    fontSize: 11,
    fontWeight: 900,
    padding: "2px 10px",
  },
  badgePagado:    { background: "#073f3f", color: "#02dfb1" },
  badgePendiente: { background: "#3a2807", color: "#ffb229" },
  viewButton: {
    color: "#8bbcff",
    minWidth: 0,
    padding: theme.spacing(0.5),
  },
  /* ── Dialog PDF ── */
  dialogPaper:    { background: "#080f1f", color: "#f5f7fb" },
  dialogTitle: {
    background: "#0d172b",
    borderBottom: "1px solid #1d3454",
    padding: theme.spacing(2, 3),
  },
  dialogTitleText:   { color: "#f5f7fb", fontSize: 17, fontWeight: 900 },
  dialogSubtitle:    { color: "#8fb1d8", fontSize: 12, marginTop: theme.spacing(0.5) },
  dialogContent:     { minHeight: "76vh", padding: theme.spacing(2) },
  ridePanel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    height: "76vh",
    overflow: "hidden",
    position: "relative",
  },
  rideFrame:  { background: "#ffffff", border: 0, height: "100%", width: "100%" },
  rideLoader: {
    alignItems: "center",
    color: "#acd0f6",
    display: "flex",
    gap: theme.spacing(1),
    height: "100%",
    justifyContent: "center",
  },
  dialogActions: {
    background: "#0d172b",
    borderTop: "1px solid #1d3454",
    padding: theme.spacing(1.5, 3),
  },
  closeButton: { color: "#acd0f6", textTransform: "none" },
}));

/* ── Helpers ── */
const fmt = (value) => {
  if (value == null || value === "") return "—";
  return `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getNumber = (v) =>
  [
    v?.comprobanteElectronico?.codigoEstablecimiento,
    v?.comprobanteElectronico?.puntoEmision,
    v?.comprobanteElectronico?.secuencial,
  ]
    .filter(Boolean)
    .join("-");

/* ── Cards config ── */
const buildCards = (s) => {
  if (!s) return [];
  return [
    {
      label: "Cobrado este mes",
      value: fmt(s.cobradoValor),
      sub: `${s.cobradoCantidad ?? 0} comprobantes`,
      tone: "Success",
    },
    {
      label: "Por cobrar",
      value: fmt(s.porCobrarValor),
      sub: `${s.porCobrarCantidad ?? 0} pendientes`,
      tone: "Warning",
    },
    {
      label: "Ingresos del mes",
      value: fmt(s.ingresosMes),
      sub: "facturas emitidas",
      tone: "Success",
    },
    {
      label: "Gastos del mes",
      value: fmt(s.valorGatos),
      sub: `${s.totalGastos ?? 0} comprobantes`,
      tone: "Danger",
    },
    {
      label: "Utilidad neta",
      value: fmt(s.utilidadNeta),
      sub: "ingresos − gastos",
      tone: "Info",
    },
    {
      label: "Notas de crédito",
      value: fmt(s.valorNC),
      sub: `${s.totalNC ?? 0} emitidas`,
      tone: "Info",
    },
  ];
};

/* ── Columnas tabla ── */
const buildColumns = (classes, openRide) => [
  {
    key: "numero",
    label: "Número",
    render: (row) => <span className={classes.number}>{getNumber(row)}</span>,
  },
  {
    key: "fecha",
    label: "Fecha",
    render: (row) => fmtDate(row.comprobanteElectronico?.fechaEmision),
  },
  {
    key: "cliente",
    label: "Cliente",
    render: (row) => (
      <span className={classes.emisor}>
        {row.comprobanteElectronico?.razonSocialComprador ||
          row.comprobanteElectronico?.razonSocial ||
          "—"}
      </span>
    ),
  },
  {
    key: "tipo",
    label: "Tipo",
    render: (row) =>
      row.comprobanteElectronico?.tipoComprobanteElectronico?.nombreComprobante || "—",
  },
  {
    key: "total",
    label: "Total",
    align: "right",
    render: (row) => fmt(row.comprobanteElectronico?.valorTotal),
  },
  {
    key: "estado",
    label: "Estado",
    render: (row) => {
      const estado = row.comprobanteElectronico?.estadoContable;
      if (!estado) return <span className={classes.badge}>Sin estado</span>;
      const pagado = estado === "PAGADO";
      return (
        <span
          className={`${classes.badge} ${
            pagado ? classes.badgePagado : classes.badgePendiente
          }`}
        >
          {estado}
        </span>
      );
    },
  },
  {
    key: "acciones",
    label: "Acciones",
    render: (row) => (
      <Tooltip title="Ver comprobante (PDF)">
        <Button className={classes.viewButton} onClick={() => openRide(row)}>
          <VisibilityIcon fontSize="small" />
        </Button>
      </Tooltip>
    ),
  },
];

/* ── Componente ── */
const PagosCobrosView = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const {
    stats,
    authorizedVouchers,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    mes,
    anio,
    onMesChange,
    onAnioChange,
  } = usePagosCobrosView();

  const cards = buildCards(stats);

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [rideUrl, setRideUrl] = useState("");
  const [rideLoading, setRideLoading] = useState(false);

  const openRide = async (voucher) => {
    const claveAcceso = voucher.comprobanteElectronico?.claveAcceso;
    setSelectedVoucher(voucher);
    setPdfOpen(true);
    setRideLoading(true);
    if (rideUrl) { window.URL.revokeObjectURL(rideUrl); setRideUrl(""); }

    try {
      const response = await clientBackendPDF.get(
        `/core/utilidades/ebox/generar/ride/claveAcceso/${claveAcceso}`,
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      setRideUrl(window.URL.createObjectURL(blob));
    } catch {
      enqueueSnackbar("No se pudo cargar el RIDE del comprobante.", { variant: "error" });
    } finally {
      setRideLoading(false);
    }
  };

  const closeRide = () => {
    setPdfOpen(false);
    setSelectedVoucher(null);
    if (rideUrl) { window.URL.revokeObjectURL(rideUrl); setRideUrl(""); }
  };

  const columns = buildColumns(classes, openRide);

  return (
    <div className={classes.root}>
      <SelectorMesAnio
        mes={mes}
        anio={anio}
        onMesChange={onMesChange}
        onAnioChange={onAnioChange}
      />

      {/* ── Tarjetas resumen ── */}
      {cards.length > 0 && (
        <div className={classes.cards}>
          {cards.map((c) => (
            <div className={classes.card} key={c.label}>
              <Typography className={classes.cardLabel}>{c.label}</Typography>
              <Typography className={`${classes.cardValue} ${classes[`cardValue${c.tone}`]}`}>
                {c.value}
              </Typography>
              <Typography className={classes.cardSub}>{c.sub}</Typography>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabla comprobantes autorizados ── */}
      <ContableTable
        columns={columns}
        rows={authorizedVouchers.comprobantes || authorizedVouchers.content || []}
        totalElements={authorizedVouchers.totalElements || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        loading={authorizedVouchers.loading}
        error={authorizedVouchers.error}
        emptyMessage="No existen comprobantes para mostrar."
        loadingMessage="Cargando comprobantes..."
      />

      {/* ── Visor PDF ── */}
      <Dialog
        classes={{ paper: classes.dialogPaper }}
        fullWidth
        maxWidth="lg"
        onClose={closeRide}
        open={pdfOpen}
      >
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography className={classes.dialogTitleText}>
            {selectedVoucher?.comprobanteElectronico?.tipoComprobanteElectronico
              ?.nombreComprobante || "Comprobante"}{" "}
            {selectedVoucher ? getNumber(selectedVoucher) : ""}
          </Typography>
          <Typography className={classes.dialogSubtitle}>
            {selectedVoucher?.comprobanteElectronico?.razonSocialComprador ||
              selectedVoucher?.comprobanteElectronico?.razonSocial || ""}
          </Typography>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <div className={classes.ridePanel}>
            {rideLoading && (
              <div className={classes.rideLoader}>
                <CircularProgress color="inherit" size={24} />
                <span>Cargando RIDE...</span>
              </div>
            )}
            {!rideLoading && rideUrl && (
              <iframe
                className={classes.rideFrame}
                src={rideUrl}
                title="RIDE comprobante"
              />
            )}
            {!rideLoading && !rideUrl && (
              <div className={classes.rideLoader}>No se pudo mostrar el RIDE.</div>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button className={classes.closeButton} onClick={closeRide}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PagosCobrosView;
